// Single registry of admin navigation + page metadata.
// Used by the sidebar, the header (title/breadcrumbs), route guards and tests
// so that visibility, titles and access rules cannot drift apart.

import {
  HiOutlineArrowsRightLeft,
  HiOutlineBanknotes,
  HiOutlineBuildingOffice2,
  HiOutlineChartBarSquare,
  HiOutlineClock,
  HiOutlineCog6Tooth,
  HiOutlineDocumentChartBar,
  HiOutlineHome,
  HiOutlineMegaphone,
  HiOutlineReceiptPercent,
  HiOutlineUsers,
} from "react-icons/hi2";
import { BsCashCoin, BsCreditCard2Front } from "react-icons/bs";
import { FiActivity, FiCpu } from "react-icons/fi";
import { IoAirplaneOutline } from "react-icons/io5";
import { CAPABILITY, can } from "../auth/permissions";

// Each page: path, i18n title key, capability needed, optional parent (for
// breadcrumbs) and whether it appears in the sidebar.
export const PAGES = Object.freeze([
  { path: "/", titleKey: "nav.dashboard", capability: CAPABILITY.DASHBOARD_VIEW, group: "overview", icon: HiOutlineHome, nav: true, end: true },
  { path: "/Reports", titleKey: "nav.reports", capability: CAPABILITY.REPORTS_VIEW, group: "overview", icon: HiOutlineDocumentChartBar, nav: true },

  { path: "/AutoBook", titleKey: "nav.autoBook", capability: CAPABILITY.AUTOBOOK_VIEW, group: "operations", icon: HiOutlineClock, nav: true },
  { path: "/TopUps", titleKey: "nav.topUps", capability: CAPABILITY.TOPUPS_VIEW, group: "operations", icon: BsCreditCard2Front, nav: true },
  { path: "/FundRequests", titleKey: "nav.fundRequests", capability: CAPABILITY.FUND_REQUESTS_VIEW, group: "operations", icon: BsCashCoin, nav: true },
  { path: "/Transactions", titleKey: "nav.transactions", capability: CAPABILITY.TRANSACTIONS_VIEW, group: "operations", icon: HiOutlineReceiptPercent, nav: true },
  { path: "/AirlineCredit", titleKey: "nav.airlineCredit", capability: CAPABILITY.AIRLINE_CREDIT_VIEW, group: "operations", icon: HiOutlineBanknotes, nav: true },

  { path: "/Agencies", titleKey: "nav.agencies", capability: CAPABILITY.AGENCIES_VIEW, group: "management", icon: HiOutlineBuildingOffice2, nav: true },
  { path: "/Agencies/AddAgency", titleKey: "nav.addAgency", capability: CAPABILITY.AGENCIES_MANAGE, parent: "/Agencies" },
  { path: "/Agencies/:id", titleKey: "nav.agencyDetails", capability: CAPABILITY.AGENCIES_VIEW, parent: "/Agencies" },
  { path: "/Users", titleKey: "nav.users", capability: CAPABILITY.USERS_VIEW, group: "management", icon: HiOutlineUsers, nav: true },
  { path: "/Users/AddUser", titleKey: "nav.addUser", capability: CAPABILITY.USERS_MANAGE, parent: "/Users" },
  { path: "/Users/:id", titleKey: "nav.userDetails", capability: CAPABILITY.USERS_VIEW, parent: "/Users" },
  { path: "/Airlines", titleKey: "nav.airlines", capability: CAPABILITY.AIRLINES_VIEW, group: "management", icon: IoAirplaneOutline, nav: true },
  { path: "/Airlines/AddAirline", titleKey: "nav.addAirline", capability: CAPABILITY.AIRLINES_MANAGE, parent: "/Airlines" },
  { path: "/Airlines/:id", titleKey: "nav.airlineDetails", capability: CAPABILITY.AIRLINES_VIEW, parent: "/Airlines" },
  { path: "/Currencies", titleKey: "nav.currencies", capability: CAPABILITY.CURRENCIES_VIEW, group: "management", icon: HiOutlineChartBarSquare, nav: true },
  { path: "/Currencies/AddCurrency", titleKey: "nav.addCurrency", capability: CAPABILITY.CURRENCIES_MANAGE, parent: "/Currencies" },
  { path: "/Currencies/:id", titleKey: "nav.currencyDetails", capability: CAPABILITY.CURRENCIES_VIEW, parent: "/Currencies" },
  { path: "/Ads", titleKey: "nav.ads", capability: CAPABILITY.ADS_MANAGE, group: "management", icon: HiOutlineMegaphone, nav: true },
  { path: "/Ads/AddAd", titleKey: "nav.addAd", capability: CAPABILITY.ADS_MANAGE, parent: "/Ads" },
  { path: "/Ads/:id", titleKey: "nav.adDetails", capability: CAPABILITY.ADS_MANAGE, parent: "/Ads" },

  { path: "/AI", titleKey: "nav.platformSettings", capability: CAPABILITY.PLATFORM_SETTINGS_VIEW, group: "system", icon: FiCpu, nav: true },
  { path: "/FxRates", titleKey: "nav.fxRates", capability: CAPABILITY.FX_RATES_VIEW, group: "system", icon: HiOutlineArrowsRightLeft, nav: true },
  { path: "/Logs", titleKey: "nav.logs", capability: CAPABILITY.LOGS_VIEW, group: "system", icon: FiActivity, nav: true },
  { path: "/Profile/Password", titleKey: "nav.password", capability: CAPABILITY.PROFILE_PASSWORD, group: "system", icon: HiOutlineCog6Tooth, nav: true },
]);

export const NAV_GROUPS = Object.freeze([
  { id: "overview", labelKey: "navGroups.overview" },
  { id: "operations", labelKey: "navGroups.operations" },
  { id: "management", labelKey: "navGroups.management" },
  { id: "system", labelKey: "navGroups.system" },
]);

// Sidebar groups filtered by role; empty groups are dropped.
export const getNavigationGroups = (role) =>
  NAV_GROUPS.map((group) => ({
    ...group,
    items: PAGES.filter(
      (page) => page.nav && page.group === group.id && can(role, page.capability),
    ),
  })).filter((group) => group.items.length > 0);

const toSegments = (path) => path.split("/").filter(Boolean);

const matchesPattern = (pattern, pathname) => {
  const patternSegments = toSegments(pattern);
  const pathSegments = toSegments(pathname);
  if (patternSegments.length !== pathSegments.length) {
    return false;
  }
  return patternSegments.every((segment, index) =>
    segment.startsWith(":")
      ? pathSegments[index].length > 0
      : segment.toLowerCase() === pathSegments[index].toLowerCase(),
  );
};

// Finds the page definition for a concrete pathname. Static patterns win over
// dynamic ones ("/Users/AddUser" before "/Users/:id").
export const findPage = (pathname) => {
  const normalized = pathname || "/";
  const candidates = PAGES.filter((page) => matchesPattern(page.path, normalized));
  if (!candidates.length) {
    return null;
  }
  return (
    candidates.find((page) => !page.path.includes(":")) || candidates[0]
  );
};

// Breadcrumb trail (root first) for a pathname.
export const getBreadcrumbs = (pathname) => {
  const trail = [];
  let page = findPage(pathname);
  while (page) {
    trail.unshift(page);
    page = page.parent ? findPage(page.parent) : null;
  }
  return trail;
};

// The sidebar item that should appear active for a pathname.
export const getActiveNavPath = (pathname) => {
  const trail = getBreadcrumbs(pathname);
  const navPage = [...trail].reverse().find((page) => page.nav);
  return navPage ? navPage.path : null;
};

// First page the role may open; used for the post-login landing page and the
// "go home" action on the not-permitted screen.
export const getDefaultPathForRole = (role) => {
  const page = PAGES.find((candidate) => candidate.nav && can(role, candidate.capability));
  return page ? page.path : "/login";
};
